// utils/tabs.config.tsx
import { Nullable } from "../../../Types/Common";
import { EmployeeDetails } from "../../../Types/UserProfileDetails";
import { IUser } from "../../login/types";
import { EmployeeTab, TabConfigItem, TabConfigKey } from "./types";
import EmployeeAboutTab from "../components/TabContent/EmployeeAboutTab";
import EmployeeEducationInfo from "../components/TabContent/EmployeeEducationTab";
import EmployeeExitTab from "../components/TabContent/EmployeeExitTab";
import EmployeeFinancialInfo from "../components/TabContent/EmployeeFinancialTab";
import EmployeeJobInfo from "../components/TabContent/EmployeeJobTab";
import EmployeeWorkInfo from "../components/TabContent/EmployeeWorkTab";
import EmployeeAssetsTab from "../components/TabContent/EmployeeAssetsTab";
import EmployeeAttendanceSummaryTab from "../components/TabContent/EmployeeAttendanceSummaryTab";
import ViewAttendance from "../../attendance/components";
import ViewConveyance from "../../conveyance/listConvenyance/components";
import LeavesUser from "../../leave/components";
import ManagerZoneContainer from "../../pettyCash/ManagerZone/components";
import ImbrusementContainer from "../../reimbursement/components";
import TeamMembersAttendance from "../../teamMembers/attendanceList/component";
import TeamMembersConveyanceContainer from "../../teamMembers/conveyanceList/components";
import TeamMembersLeave from "../../teamMembers/leaveList/components";
import ViewTeamMembers from "../../teamMembers/teamMembersList/component";
import {
  educationInformationModal,
  familyInformationModal,
  financialInformationModal,
  personalInformationModal,
  workInformationModal,
} from "../../../utils/Constant";
import { areAllValuesEmpty } from "./format";

const norm = (v?: string | null) => (v ?? "").trim().toLowerCase();

export const getEmployeeTabs = (
  employee: EmployeeDetails | null,
  loggedInEmployee?: Nullable<IUser>
): EmployeeTab[] => {
  const tabs: EmployeeTab[] = [
    {
      id: 1,
      name: "About",
      children: [
        { id: 101, name: "Personal Details", component: EmployeeAboutTab },
        { id: 102, name: "Work Information", component: EmployeeWorkInfo },
        {
          id: 103,
          name: "Education Information",
          component: EmployeeEducationInfo,
        },
      ],
    },
    {
      id: 2,
      name: "Job & Finance",
      children: [
        { id: 201, name: "Job Information", component: EmployeeJobInfo },
        {
          id: 202,
          name: "Finance Information",
          component: EmployeeFinancialInfo,
          condition: (e: EmployeeDetails) => {
            if (!loggedInEmployee || !e) return false;
            const loggedInId = Number(loggedInEmployee.id);
            const viewedEmployeeId = Number(e.jobInformation?.userId);
            const privilegedIds = [1, 29, 1609];
            const isShowRoles = ["admin", "hr", "account"].includes(
              loggedInEmployee.user_type?.trim()?.toLowerCase()
            );
            return (
              privilegedIds.includes(loggedInId) ||
              loggedInId === viewedEmployeeId ||
              isShowRoles
            );
          },
        },
        {
          id: 203,
          name: "Exit Info",
          component: EmployeeExitTab,
          condition: (e: EmployeeDetails) =>
            !!e?.user_exit_info && e.user_exit_info.length > 0,
        },
      ],
    },
    {
      id: 3,
      name: "Expense",
      children: [
        { id: 301, name: "Conveyance", component: ViewConveyance },
        { id: 302, name: "Reimbursements", component: ImbrusementContainer },
        {
          id: 303,
          name: "Petty Cash",
          component: ManagerZoneContainer,
          condition: (e: EmployeeDetails) =>
            e?.jobInformation?.pettyCash === "yes",
        },
      ],
    },
    {
      id: 4,
      name: "Time",
      children: [
        { id: 401, name: "Attendance", component: ViewAttendance },
        { id: 402, name: "Leaves", component: LeavesUser },
        {
          id: 403,
          name: "Attendance Summary",
          component: EmployeeAttendanceSummaryTab,
          condition: () => !loggedInEmployee || ["admin", "hr"].includes(norm(loggedInEmployee.user_type)),
        },
      ],
    },
  ];

  // Add Team tab only if the viewed employee is admin/manager/hr
  if (
    ["admin", "manager", "hr", "account"].includes(
      norm(employee?.jobInformation?.userType)
    )
  ) {
    tabs.push({
      id: 5,
      name: "Team",
      children: [
        { id: 501, name: "Team Members", component: ViewTeamMembers },
        {
          id: 502,
          name: "Attendance Request",
          component: TeamMembersAttendance,
        },
        { id: 503, name: "Leave Request", component: TeamMembersLeave },
        {
          id: 504,
          name: "Conveyance Report",
          component: TeamMembersConveyanceContainer,
        },
      ],
    });
  }

  if (!loggedInEmployee || ["admin", "hr"].includes(norm(loggedInEmployee.user_type)) || Number(loggedInEmployee.id) === Number(employee?.jobInformation?.userId)) {
    tabs.push({ id: 6, name: "Assets", children: [{ id: 601, name: "Assigned Assets", component: EmployeeAssetsTab }] });
  }

  // Hide "About" when logged-in user is a manager (as per your requirement)
  const visibleTabs =
    ["manager", "account"].includes(norm(loggedInEmployee?.user_type)) &&
    Number(loggedInEmployee?.id) !== Number(employee?.jobInformation?.userId)
      ? tabs.filter((t) => t.id !== 1)
      : tabs;

  // Apply child conditions safely (no non-null assertion)
  return visibleTabs.map((tab) => ({
    ...tab,
    children: tab.children.filter(
      (child) =>
        !child.condition || (employee ? child.condition(employee) : false)
    ),
  }));
};

export const TAB_CONFIG: Record<TabConfigKey, TabConfigItem> = {
  "finance information": {
    modalType: financialInformationModal,
    tooltip: "Add Financial Information",
    isEmpty: (details: EmployeeDetails) =>
      areAllValuesEmpty(details?.employeeFinancialInformation, [
        "id",
        "user_id",
        "created_at",
        "updated_at",
      ]),
  },
  "family information": {
    modalType: familyInformationModal,
    tooltip: "Add Family Information",
    isEmpty: (details: EmployeeDetails) =>
      areAllValuesEmpty(details?.employeeFamilyInformation, [
        "userId",
        details.employeeFamilyInformation?.maritalStatus === "married"
          ? ""
          : "maritalStatus",
      ]),
  },
  "job information": {
    modalType: "jobInformationModal",
    tooltip: "Add Official Information",
    isEmpty: (details: EmployeeDetails) =>
      areAllValuesEmpty(details?.jobInformation, [
        "center",
        "managerName",
        "profile",
        "userId",
        "additionalManagerNames",
        "additionalManagerEmails",
        "additionalManagerIds",
        "managerName",
        "managerId",
        "managerEmail",
        "underProbhation",
        "role",
        "pettyCash",
      ]),
  },
  "education information": {
    modalType: educationInformationModal,
    tooltip: "Add Education Information",
    isEmpty: () => true,
  },
  "work information": {
    modalType: workInformationModal,
    tooltip: "Add Education Information",
    isEmpty: () => true,
  },
  "personal details": {
    modalType: personalInformationModal,
    tooltip: "Add Education Information",
    isEmpty: (details: EmployeeDetails) =>
      areAllValuesEmpty(details.employeePersonalInformation, [
        "userType",
        "callingSystem",
        "status",
        "userId",
      ]),
  },
};
